/** Store → Returns: requests from customers; approve / reject / receive (restock) / refund / close. Route /sites/:id/returns */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Descriptions, Drawer, Empty, Input, Popconfirm, Space, Spin, Table, Tabs, Typography, message } from 'antd';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, listOf, money, sitePath, toastErr, totalOf, useSite } from './common';
import { MoneyInput, Thumb } from './ShopCommon';
import { CarrierSelect, RETURN_STATUSES, ReturnTag, useCarriers } from './ShopLogistics';

const { Text } = Typography;

const ReturnDrawer = ({ rid, onClose, onChanged }) => {
    const { t } = useTranslation();
    const { api, siteId } = useSite();
    const carriers = useCarriers(api);
    const [r, setR] = useState(null);
    const [busy, setBusy] = useState(null);
    const [note, setNote] = useState('');
    const [track, setTrack] = useState({ carrier: undefined, tracking_number: '' });
    const [restock, setRestock] = useState(true);
    const [amount, setAmount] = useState(null);

    const load = async () => {
        try {
            const d = await api.get(`/returns/${rid}`);
            setR(d);
            setNote(d.merchant_note || '');
            setAmount(null);
        } catch (e) { toastErr(e); onClose(); }
    };
    useEffect(() => { if (rid) { setR(null); load(); } }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps

    const act = async (action, body = {}) => {
        setBusy(action);
        try { await api.post(`/returns/${rid}/${action}`, body); message.success(t('Updated')); await load(); onChanged(); } catch (e) { toastErr(e); } finally { setBusy(null); }
    };

    const order = r?.order || {};
    const cur = order.currency || r?.currency;
    const items = listOf(r?.items).map((ri) => ({ ...ri, item: listOf(order.items).find((x) => x.id === ri.order_item_id) }));
    const suggested = items.reduce((s, ri) => s + (ri.item && ri.item.quantity ? Math.floor(((ri.item.line_total ?? ri.item.price * ri.item.quantity) - (ri.item.discount_allocation || 0)) * ri.quantity / ri.item.quantity) : 0), 0);

    return (
        <Drawer open={!!rid} onClose={onClose} width={Math.min(560, window.innerWidth)} destroyOnHidden
            title={r ? <Space>{t('Return')} #{r.id}<ReturnTag value={r.status} /></Space> : t('Return')}>
            {!r ? <Spin /> : (
                <>
                    <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
                        <Descriptions.Item label={t('Order', { context: 'shop' })}><Link to={sitePath(siteId, `orders/${r.order_id}`)} onClick={onClose}>{order.name || r.order_name || `#${r.order_id}`}</Link></Descriptions.Item>
                        <Descriptions.Item label={t('Customer')}>{order.email || r.email || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Requested')}>{fmtDate(r.created_at)}</Descriptions.Item>
                        {r.return_tracking?.tracking_number && (
                            <Descriptions.Item label={t('Return shipment')}>
                                {r.return_tracking.tracking_url ? <a href={r.return_tracking.tracking_url} target="_blank" rel="noopener noreferrer">{r.return_tracking.tracking_number}</a> : r.return_tracking.tracking_number}
                            </Descriptions.Item>
                        )}
                        {r.restocked && <Descriptions.Item label={t('Stock')}>{t('Restocked')}</Descriptions.Item>}
                    </Descriptions>
                    <Table size="small" rowKey="order_item_id" pagination={false} dataSource={items} style={{ marginBottom: 12 }} columns={[
                        { title: t('Item', { context: 'shop' }), key: 'i', render: (_, x) => <Space><Thumb src={x.item?.image?.src} size={32} /><div><div>{x.item?.title || `#${x.order_item_id}`}</div>{x.reason && <Text type="secondary" style={{ fontSize: 12 }}>{x.reason}</Text>}</div></Space> },
                        { title: t('Qty', { context: 'shop' }), dataIndex: 'quantity', width: 60 },
                    ]} />
                    {r.customer_note && <Alert type="info" style={{ marginBottom: 12 }} message={r.customer_note} />}

                    {['requested', 'approved'].includes(r.status) && (
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Note to the customer')} autoSize={{ minRows: 2 }} style={{ marginBottom: 12 }} />
                    )}
                    {r.status === 'requested' && (
                        <>
                            <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                                <CarrierSelect carriers={carriers} value={track.carrier} onChange={(carrier) => setTrack((x) => ({ ...x, carrier }))} style={{ width: 200 }} />
                                <Input placeholder={t('Return tracking number (optional)')} value={track.tracking_number} onChange={(e) => setTrack((x) => ({ ...x, tracking_number: e.target.value.trim() }))} />
                            </Space.Compact>
                            <Space>
                                <Button type="primary" loading={busy === 'approve'} onClick={() => act('approve', { note, ...(track.tracking_number ? { return_tracking: { carrier: track.carrier || 'other', tracking_number: track.tracking_number } } : {}) })}>{t('Approve')}</Button>
                                <Popconfirm title={t('Reject this return?')} onConfirm={() => act('reject', { note })}><Button danger loading={busy === 'reject'}>{t('Reject')}</Button></Popconfirm>
                            </Space>
                        </>
                    )}
                    {r.status === 'approved' && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Space>
                                <Checkbox checked={restock} onChange={(e) => setRestock(e.target.checked)}>{t('Restock items')}</Checkbox>
                                <Button loading={busy === 'receive'} onClick={() => act('receive', { restock })}>{t('Mark received')}</Button>
                            </Space>
                            <Popconfirm title={t('Reject this return?')} onConfirm={() => act('reject', { note })}><Button danger size="small" loading={busy === 'reject'}>{t('Reject')}</Button></Popconfirm>
                        </Space>
                    )}
                    {['approved', 'received'].includes(r.status) && (
                        <Card size="small" style={{ marginTop: 12 }} title={t('Refund')}>
                            <Space wrap>
                                <MoneyInput currency={cur} value={amount ?? suggested} onChange={setAmount} style={{ width: 160 }} />
                                <Popconfirm title={t('Refund {{amount}}', { amount: money(amount ?? suggested, cur) })} onConfirm={() => act('refund', { amount: amount ?? suggested, notify: true })}>
                                    <Button type="primary" danger loading={busy === 'refund'} disabled={!(amount ?? suggested)}>{t('Refund')}</Button>
                                </Popconfirm>
                            </Space>
                        </Card>
                    )}
                    {r.status !== 'closed' && (
                        <div style={{ marginTop: 16 }}>
                            <Popconfirm title={t('Close this return?')} onConfirm={() => act('close')}><Button type="link" style={{ padding: 0 }} loading={busy === 'close'}>{t('Close return')}</Button></Popconfirm>
                        </div>
                    )}
                </>
            )}
        </Drawer>
    );
};

const Returns = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [status, setStatus] = useState('requested');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [soon, setSoon] = useState(false);
    const [open, setOpen] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let live = true;
        setLoading(true);
        api.get('/returns', { status: status || undefined, page })
            .then((d) => live && setData({ items: listOf(d), total: totalOf(d) }))
            .catch((e) => { if (live) { if (e.status === 404) setSoon(true); else toastErr(e); setData({ items: [], total: 0 }); } })
            .finally(() => live && setLoading(false));
        return () => { live = false; };
    }, [api, status, page, tick]);

    if (soon) return <Card><Empty description={t('Returns are coming soon')} /></Card>;
    return (
        <Card title={t('Returns')} styles={{ body: { paddingTop: 0 } }}>
            <Tabs activeKey={status} onChange={(k) => { setStatus(k); setPage(1); }}
                items={[...RETURN_STATUSES.map((s) => ({ key: s, label: <ReturnLabel s={s} /> })), { key: '', label: t('All') }]} />
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 640 }}
                onRow={(r) => ({ onClick: () => setOpen(r.id), style: { cursor: 'pointer' } })}
                pagination={{ current: page, pageSize: 25, total: data.total, hideOnSinglePage: true, showSizeChanger: false, onChange: setPage }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No returns')} /> }}
                columns={[
                    { title: '#', dataIndex: 'id', width: 70 },
                    { title: t('Order', { context: 'shop' }), dataIndex: 'order_name', width: 110 },
                    { title: t('Customer'), dataIndex: 'email' },
                    { title: t('Items'), key: 'n', width: 80, render: (_, r) => listOf(r.items).reduce((s, i) => s + (i.quantity || 0), 0) },
                    { title: t('Status'), dataIndex: 'status', width: 120, render: (v) => <ReturnTag value={v} /> },
                    { title: t('Requested'), dataIndex: 'created_at', width: 170, render: (v) => fmtDate(v) },
                ]} />
            <ReturnDrawer rid={open} onClose={() => setOpen(null)} onChanged={() => setTick((x) => x + 1)} />
        </Card>
    );
};

const ReturnLabel = ({ s }) => {
    const { t } = useTranslation();
    return t({ requested: 'Requested', approved: 'Approved', rejected: 'Rejected', received: 'Received', refunded: 'Refunded', closed: 'Closed' }[s] || s, { context: 'return' });
};

export default Returns;
